import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MarkdownContent from '../../components/content/MarkdownContent';

// Frontend (CloudFront/S3) and API live on separate hosts in non-local envs
// (staging: env-9b2d → env-7f3a; production: www → api). A bare same-origin
// /api/* path resolves to the SPA bucket and gets rewritten to index.html by
// the CloudFront 403/404 → /index.html fallback, which then fails JSON.parse.
// Always prefix VITE_API_URL; fall back to '' so same-origin still works for
// the dev proxy (vite.config dev server proxies /api/* to localhost:3030).
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * LegalDocumentPage (SCRUM-2203 — MVP 1.40.3)
 *
 * Fetches the current published version of a legal document by slug from
 * /api/v1/legal-documents/:slug and renders it through the shared
 * MarkdownContent renderer. Routes that point to this page pass the slug as
 * a route prop (preferred) or via /:slug param.
 *
 * - If the API returns 404 / NOT_PUBLISHED → render a neutral "not available
 *   yet" placeholder. (eula and guide-agreement land here at launch — their
 *   v1.0 will be created via the super-admin UI.)
 * - On unexpected errors → render an inline error notice (consistent with
 *   StaticPage).
 */
export default function LegalDocumentPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;

  const [state, setState] = useState({ status: 'loading', doc: null, error: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/legal-documents/${encodeURIComponent(slug)}`, {
          credentials: 'include',
        });
        if (cancelled) {
          return;
        }
        if (res.status === 404) {
          setState({ status: 'not_published', doc: null, error: null });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', doc: null, error: `Request failed (${res.status})` });
          return;
        }
        const json = await res.json();
        const doc = (json && json.data) || null;
        if (!doc) {
          setState({ status: 'not_published', doc: null, error: null });
          return;
        }
        setState({ status: 'ready', doc, error: null });
      } catch (err) {
        if (cancelled) {
          return;
        }
        setState({ status: 'error', doc: null, error: err && err.message ? err.message : 'Failed to load' });
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8 sm:py-12">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 lg:p-12">
            {state.status === 'loading' && (
              <p className="text-gray-500">Loading…</p>
            )}

            {state.status === 'error' && (
              <>
                <h1 className="text-2xl font-bold text-red-600 mb-4">Error Loading Page</h1>
                <p className="text-gray-600">{state.error}</p>
              </>
            )}

            {state.status === 'not_published' && (
              <>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 border-b pb-4">
                  Not available yet
                </h1>
                <p className="text-gray-700 leading-relaxed">
                  This document hasn&apos;t been published yet. Please check back soon.
                </p>
              </>
            )}

            {state.status === 'ready' && state.doc && (
              <>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 border-b pb-4">
                  {state.doc.title}
                </h1>
                {state.doc.effective_date && (
                  <p className="text-sm text-gray-500 mb-6">
                    Effective: {new Date(state.doc.effective_date).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
                    })}
                    {state.doc.version_number ? ` · v${state.doc.version_number}` : ''}
                  </p>
                )}
                <MarkdownContent>{state.doc.body}</MarkdownContent>
              </>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
